import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../../rent/model/esignatory';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';
import { configureESignatoryContactValidation } from '../../../rent/helper/esignatory-form-validation';

@Component({
  standalone: false,
  selector: 'app-esign-amortization-plan-document',
  templateUrl: './esign-amortization-plan-document.component.html',
  styleUrls: ['./esign-amortization-plan-document.component.scss'],
  providers: [NavigationService],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ESignAmortizationPlanDocumentComponent implements OnDestroy, OnInit {
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _fc: FormControlExFactory,
    private readonly _navigation: NavigationService,
    private readonly _nativeWindow: NativeWindowService,
    private readonly _amortizationPlanService: AmortizationPlanService,
    private readonly _fb: FormBuilder,
    private readonly _messageService: MessageService
  ) {
    this._subscriptions = new Subscription();
    this.form = this._fb.group({
      expirationDate: this._fc.createMandatory(
        'ESIGNATURE.EXPIRATION_DATE',
        null,
        'ESIGNATURE.HELP_TEXT.EXPIRATION_DATE'
      ),
      errandDescription: this._fc.createMandatory('ESIGNATURE.DESCRIPTION', ''),
      informWhenSigned: this._fc.create('ESIGNATURE.INFORM_WHEN_SIGNED', true),
      informEmailAddress: this._fc.create('ESIGNATURE.INFORM_EMAIL_ADRESSES', ''),
      signatories: this._fb.array([]),
      attachments: this._fb.array([]),
    });
  }

  readonly form: FormGroup;

  amortizationPlanId: string;
  companyName: string;
  debtorContactName: string;
  expirationDate: LocalDate;

  readonly civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat = 'AAAAMMDD-NNNN';

  readonly signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  readonly informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL_AND_SMS', InformationChannelKind.EmailAndSms),
  ];

  readonly signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.NOT_SPECIFIED', SigningRoleKind.NotSpecified),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
    new SelectionItem('LEASE_AGREEMENT.CREDITOR', SigningRoleKind.Guarantor),
  ];

  readonly signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  private readonly _subscriptions: Subscription;

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(control => control as FormGroup);
  }

  ngOnInit(): void {
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            const id = map.get('amortizationPlanId');
            if (id) {
              this.amortizationPlanId = id;
              return this._amortizationPlanService.getDataForESign(id);
            }

            return of(null);
          })
        )
        .subscribe(data => this.setESignData(data))
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  cancel(): void {
    this._navigation.navigateBack();
  }

  previewForm(): void {
    if (!this.amortizationPlanId) {
      return;
    }

    const tab = this._nativeWindow.openTab();

    this.AddSubscription(
      this._amortizationPlanService.getPdf(this.amortizationPlanId).subscribe({
        next: file => this._nativeWindow.openFile(file, `${this.amortizationPlanId}.pdf`, tab),
        error: error => {
          tab?.close?.();
          this._messageService.showError(error);
        },
      })
    );
  }

  newSignatory(): void {
    this.addSignatory(new ESignatory());
  }

  deleteSignatory(index: number): void {
    const signatories = this.form.controls['signatories'] as FormArray;
    signatories.removeAt(index);
  }

  save(): void {
    if (this.form.disabled) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const context = this.form.value;
    context.amortizationPlanId = this.amortizationPlanId;
    context.attachments = (this.form.controls['attachments'] as FormArray).controls.map(control => control.value.data);

    this.form.disable();

    this.AddSubscription(
      this._amortizationPlanService.sendForESign(context).subscribe(
        () => this._navigation.navigateBack(),
        error => {
          this.form.enable();
          this._messageService.showError(error);
        }
      )
    );
  }

  protected AddSubscription(subscription: Subscription): void {
    if (!subscription) {
      throw new Error('Null reference. Object not set.');
    }

    this._subscriptions.add(subscription);
  }

  private addSignatory(signatory: ESignatory): void {
    const signatories = this.form.controls['signatories'] as FormArray;
    const signatoryForm = this._fb.group({
        id: this._fc.createHidden(signatory.id),
        civicNumber: this._fc.create('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber),
        email: this._fc.createEmail('ESIGNATURE.SIGNATORY.EMAIL', signatory.email),
        informationChannel: this._fc.createMandatory('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel),
        signatureType: this._fc.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        name: this._fc.createMandatory('ESIGNATURE.SIGNATORY.NAME', signatory.name),
        phone: this._fc.create('ESIGNATURE.SIGNATORY.PHONE', signatory.phone),
        signOrder: this._fc.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fc.createMandatory('COMMON.ROLE', signatory.signingRole),
        signatureReminderSetting: this._fc.createMandatory(
          'ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING',
          signatory.signatureReminderSetting
        ),
      });
    signatories.push(signatoryForm);
    this.AddSubscription(configureESignatoryContactValidation(signatoryForm));
  }

  private setESignData(context: any): void {
    if (!context) {
      return;
    }

    this.companyName = context.companyName;
    this.debtorContactName = context.debtorContactName;
    this.expirationDate = LocalDate.fromDate(LocalDate.today().addDays(context.defaultResponseTime));

    this.form.controls['expirationDate'].setValue(this.expirationDate);
    this.form.controls['errandDescription'].setValue(context.errandDescription);
    this.form.controls['informEmailAddress'].setValue(context.informEmailAddress);

    for (const signatory of context.signatories ?? []) {
      this.addSignatory(signatory);
    }

    const currentUserSignatory = new ESignatory();
    currentUserSignatory.civicNumber = context.currentUserCivicNumber;
    currentUserSignatory.name = context.currentUserPersonName;
    currentUserSignatory.email = context.currentUserEmailAddress;
    currentUserSignatory.phone = context.currentUserPhone;
    currentUserSignatory.informationChannel = context.currentUserInformationChannel;
    currentUserSignatory.signingRole = context.currentUserSigningRole;
    currentUserSignatory.signOrder = context.currentUserSignOrder;
    currentUserSignatory.signatureType = context.currentUserSignatureType;
    currentUserSignatory.signatureReminderSetting = context.currentUserSignatureReminderSetting;
    this.addSignatory(currentUserSignatory);

    this.form.markAllAsTouched();
  }
}
